import React from 'react';
import { MapPin, Phone, ShieldCheck, Crosshair, Map, Store, Users, Target, ArrowRight, CheckCircle2, Calendar, Info, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL, ADDRESS_LINE_1, ADDRESS_LINE_2, FACEBOOK_URL } from '../constants/business';
export function Home() {
  useDocumentMeta('Kjergaard Sports | Firearms, Hunting Gear & Sporting Goods in Lake Benton, Minnesota', 'Visit Kjergaard Sports in Lake Benton, Minnesota for a large in-store selection of firearms, ammunition, optics, reloading supplies, safes, hunting blinds, accessories, classes, events, and shooting sports support.');
  const features = [{
    icon: <Store className="w-8 h-8 text-brand-hunter" data-id="element-376" />,
    title: 'A Huge In-Store Selection',
    description: 'Browse thousands of firearms and a massive inventory of gear in person. We stock the volume of a big-box store with the atmosphere of a local outfitter.'
  }, {
    icon: <Users className="w-8 h-8 text-brand-hunter" data-id="element-377" />,
    title: 'Practical Product Knowledge',
    description: 'Get guidance from staff who actually use the equipment. We help you find the right tool for the field, the range, or personal defense.'
  }, {
    icon: <Target className="w-8 h-8 text-brand-hunter" data-id="element-378" />,
    title: 'Built for Hunters, Shooters & Collectors',
    description: 'From upland bird hunting to precision reloading, our inventory is curated for serious outdoorsmen and shooting sports enthusiasts.'
  }];
  const heroPoints = [{
    icon: <Store className="w-5 h-5 text-brand-leather" data-id="element-379" />,
    text: '5,000+ guns on hand'
  }, {
    icon: <ShieldCheck className="w-5 h-5 text-brand-leather" data-id="element-380" />,
    text: 'In-store service only'
  }, {
    icon: <Crosshair className="w-5 h-5 text-brand-leather" data-id="element-381" />,
    text: 'Hunting, shooting sports, CCW & collectors'
  }, {
    icon: <MapPin className="w-5 h-5 text-brand-leather" data-id="element-382" />,
    text: 'Lake Benton, Minnesota'
  }];
  const categories = [{
    title: 'Firearms',
    image: 'https://cdn.magicpatterns.com/uploads/h2HHsk76CJxRY2pmsyL8mj/firearms-wall.jpg',
    alt: 'Firearms selection at Kjergaard Sports',
    description: 'Approximately 5,000 new and used firearms on hand, including options for hunting, sport shooting, personal defense, collecting, trap, skeet, waterfowl, upland, varmints, and more.',
    link: '/firearms'
  }, {
    title: 'Ammunition',
    image: 'https://cdn.magicpatterns.com/uploads/w1nfdCex88kVGKJTVUQepJ/ammunition-wall.jpg',
    alt: 'Ammunition and reloading supplies at Kjergaard Sports',
    description: 'A large in-store ammunition selection for hunting, target shooting, trap, skeet, waterfowl, upland, turkey, big game, personal defense, and popular calibers.',
    link: '/ammunition'
  }, {
    title: 'Optics',
    image: 'https://cdn.magicpatterns.com/uploads/1zvBEqUZKRM4BZCWhxgmNi/optics-cases.jpg',
    alt: 'Scopes and optics available at Kjergaard Sports',
    description: 'Scopes, binoculars, spotting scopes, rangefinders, and optics options for hunters, target shooters, and outdoor use.',
    link: '/optics'
  }, {
    title: 'Reloading Supplies',
    image: 'https://cdn.magicpatterns.com/uploads/8918DCMwwSgbp8VrUA2ZyG/reloading-supplies.jpg',
    alt: 'Reloading supplies at Kjergaard Sports',
    description: 'Bullets, powders, primers, presses, dies, scales, tools, and reloading support for customers who enjoy building safe, accurate loads.',
    link: '/reloading-supplies'
  }, {
    title: 'Safes & Accessories',
    image: 'https://cdn.magicpatterns.com/uploads/esrRsJE1XbS5zcQi7PgR9E/safes-display.jpg',
    alt: 'Gun safes and secure storage at Kjergaard Sports',
    description: 'Safes, cleaning supplies, cases, holsters, magazines, rests, targets, clay birds, firearm maintenance items, and shooting accessories.',
    link: '/safes-accessories'
  }, {
    title: 'Hunting Gear & Outdoor Gear',
    image: 'https://cdn.magicpatterns.com/uploads/916kAQgnnbRYFKKSKvhkwD/hunting-apparel-gear.jpg',
    alt: 'Hunting apparel and outdoor gear at Kjergaard Sports',
    description: 'Apparel, boots, packs, blinds, decoys, calls, and outdoor gear for customers getting ready for the field — bird, big game, waterfowl, and turkey.',
    link: '/hunting-gear-blinds'
  }];
  const services = ['Firearm selection guidance', 'Scope and accessory installation questions', 'Call to confirm current gunsmithing or repair support', 'Minnesota Permit to Carry class information', 'Call with NFA / Form 4 questions', 'Event and demo shoot updates'];
  const events = [{
    title: 'Minnesota Permit to Carry Classes',
    description: 'In-person instruction covering safety, legal requirements, and practical fundamentals.'
  }, {
    title: 'Buffalo Ridge Demo Shoot',
    description: 'Annual opportunity to compare options, learn more, and connect with other shooting sports enthusiasts in a safe, supervised environment.'
  }, {
    title: 'Store Events & Announcements',
    description: 'Special promotions, manufacturer days, and seasonal store updates.'
  }];
  const reviews = [{
    text: 'Very friendly and the staff has a lot of knowledge. Prices are very reasonable.',
    author: 'Marty Hilfers'
  }, {
    text: 'Huge inventory with a nice store. A little out of the way, but well worth the trip.',
    author: 'Jacob Raml'
  }, {
    text: 'Fantastic experience today. Extremely friendly and helpful. Great prices too! Worth the gas to get there!',
    author: 'J D'
  }];
  return <div data-id="element-383">
      {/* HERO */}
      <section className="relative flex items-center min-h-[78vh] lg:min-h-[680px] pt-12 lg:pt-16 pb-12 lg:pb-16" data-id="element-384">
        {/* Background image */}
        <div className="absolute inset-0 z-0" data-id="element-385">
          <img src="https://cdn.magicpatterns.com/uploads/6KbpNKGWUu4crVHhGcrQiK/home-hero-store-interior.jpg" alt="Inside Kjergaard Sports — a large rural sporting goods and firearms store in Lake Benton, Minnesota" className="w-full h-full object-cover object-center" data-id="element-386" />
          {/* Left-to-right gradient: dark behind text, lighter toward the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/85 via-brand-charcoal/55 to-brand-charcoal/15" data-id="element-387"></div>
          {/* Subtle bottom fade for section transition */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-brand-charcoal/70 to-transparent" data-id="element-388"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-389">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center" data-id="element-390">
            {/* LEFT: Hero content */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="lg:col-span-7" data-id="element-391">
              <div className="flex items-center space-x-3 mb-5" data-id="element-392">
                <div className="h-px w-10 bg-brand-leather" data-id="element-393"></div>
                <span className="text-brand-bone uppercase tracking-widest text-xs sm:text-sm font-semibold drop-shadow-md" data-id="element-394">
                  Lake Benton, Minnesota
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif text-brand-offwhite mb-4 leading-[1.05] drop-shadow-lg" data-id="element-395">
                Kjergaard Sports
              </h1>

              <p className="text-xl md:text-2xl font-serif italic text-brand-leather mb-6 drop-shadow-md" data-id="element-396">
                Where the service and price are worth the trip.
              </p>

              <p className="text-base md:text-lg text-brand-cream mb-9 leading-relaxed max-w-xl drop-shadow-md" data-id="element-397">
                A rural shooting sports and hunting supply destination with
                approximately 5,000 guns on hand, plus ammunition, optics,
                reloading supplies, safes, hunting gear, accessories, and
                in-store expertise.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6" data-id="element-398">
                <Link to="/contact" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors text-base" data-id="element-399">
                  Plan Your Visit
                </Link>
                <a href={PHONE_TEL} className="border-2 border-brand-bone hover:border-brand-leather hover:bg-brand-leather/10 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors text-base flex items-center justify-center" data-id="element-400">
                  <Phone className="w-5 h-5 mr-2" data-id="element-401" />
                  Call {PHONE}
                </a>
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-offwhite/10 hover:bg-brand-offwhite/20 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors text-base flex items-center justify-center backdrop-blur-sm border border-brand-offwhite/20" data-id="element-402">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-403" />
                  Get Directions
                </a>
              </div>

              <p className="text-brand-bone/80 text-xs leading-relaxed max-w-xl drop-shadow-md" data-id="element-404">
                This website is for business information only. Kjergaard Sports
                does not conduct sales directly through this website. All
                firearm-related sales, transfers, and regulated services are
                handled in-store and in accordance with applicable laws.
              </p>
            </motion.div>

            {/* RIGHT: "Why People Make the Drive" card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.15
          }} className="lg:col-span-5" data-id="element-405">
              <div className="bg-brand-offwhite/95 backdrop-blur-md border border-brand-bone shadow-2xl rounded-sm p-7 md:p-8 lg:ml-auto lg:max-w-md" data-id="element-406">
                <div className="flex items-center space-x-3 mb-4" data-id="element-407">
                  <div className="h-px w-8 bg-brand-leather" data-id="element-408"></div>
                  <span className="text-brand-hunter uppercase tracking-widest text-xs font-semibold" data-id="element-409">
                    A Destination Store
                  </span>
                </div>

                <h2 className="text-2xl md:text-3xl font-serif text-brand-charcoal mb-6 leading-tight" data-id="element-410">
                  Why People Make the Drive
                </h2>

                <ul className="space-y-4 mb-7" data-id="element-411">
                  {heroPoints.map((point, index) => <li key={index} className="flex items-start" data-id="element-412">
                      <span className="w-9 h-9 bg-brand-bone rounded-full flex items-center justify-center mr-3 flex-shrink-0" data-id="element-413">
                        {point.icon}
                      </span>
                      <span className="text-brand-charcoal font-medium pt-1.5 leading-snug" data-id="element-414">
                        {point.text}
                      </span>
                    </li>)}
                </ul>

                <div className="pt-5 border-t border-brand-bone" data-id="element-415">
                  <div className="flex items-start mb-3" data-id="element-416">
                    <MapPin className="w-5 h-5 text-brand-hunter mr-3 flex-shrink-0 mt-0.5" data-id="element-417" />
                    <div data-id="element-418">
                      <p className="text-brand-charcoal/85 text-sm leading-snug" data-id="element-419">
                        {ADDRESS_LINE_1}
                        <br data-id="element-420" />
                        {ADDRESS_LINE_2}
                      </p>
                    </div>
                  </div>
                  <a href={PHONE_TEL} className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors" data-id="element-421">
                    <Phone className="w-4 h-4 mr-2" data-id="element-422" />
                    {PHONE}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-brand-offwhite bg-texture" data-id="element-423">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-424">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-425">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-426">
              Worth the Trip Since the First Visit
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-427">
              Kjergaard Sports is not a small generic shop. It is an enormous
              regional destination store with a deep in-store selection,
              knowledgeable service, and a practical hunting and shooting sports
              focus. Many customers travel long distances because they can
              browse a large selection in person and get guidance from people
              who understand the products.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-428">
            {features.map((feature, index) => <div key={index} className="bg-brand-cream p-8 rounded-sm border border-brand-bone shadow-sm hover:shadow-md transition-shadow" data-id="element-429">
                <div className="w-16 h-16 bg-brand-bone rounded-full flex items-center justify-center mb-6" data-id="element-430">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-serif text-brand-charcoal mb-4" data-id="element-431">
                  {feature.title}
                </h3>
                <p className="text-brand-charcoal/75 leading-relaxed" data-id="element-432">
                  {feature.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="selection" className="py-20 bg-brand-bone" data-id="element-433">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-434">
          <div className="mb-12" data-id="element-435">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-436">
              What You'll Find In Store
            </h2>
            <div className="h-1 w-20 bg-brand-leather" data-id="element-437"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-438">
            {categories.map((category, index) => <div key={index} className="bg-brand-offwhite rounded-sm overflow-hidden group border border-brand-cream shadow-sm hover:shadow-md transition-all" data-id="element-439">
                <div className="relative h-64 overflow-hidden" data-id="element-440">
                  <img src={category.image} alt={category.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-441" />
                  <div className="absolute inset-0 bg-brand-charcoal/10 group-hover:bg-transparent transition-colors" data-id="element-442"></div>
                </div>
                <div className="p-6" data-id="element-443">
                  <h3 className="text-2xl font-serif text-brand-charcoal mb-3" data-id="element-444">
                    {category.title}
                  </h3>
                  <p className="text-brand-charcoal/75 mb-6 line-clamp-3" data-id="element-445">
                    {category.description}
                  </p>
                  <Link to={category.link} className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors" data-id="element-446">
                    Learn More <ArrowRight className="w-4 h-4 ml-2" data-id="element-447" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* STORE TOUR GALLERY — bigger, asymmetric collage */}
      <section className="py-20 bg-brand-offwhite" data-id="element-448">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-449">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12" data-id="element-450">
            <div className="lg:col-span-7" data-id="element-451">
              <h2 className="text-3xl md:text-5xl font-serif text-brand-charcoal mb-4 leading-tight" data-id="element-452">
                A Store Worth the Drive
              </h2>
              <div className="h-1 w-20 bg-brand-leather mb-6" data-id="element-453"></div>
              <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-454">
                Approximately 5,000 firearms on hand. Racks of rifles and
                shotguns. Walls of ammunition and reloading components. A
                workshop built for real in-person help. This is what a serious
                Minnesota sporting goods destination looks like — take a look
                around before you make the trip.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end" data-id="element-455">
              <Link to="/store-tour" className="inline-flex items-center bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-6 py-3 rounded-sm font-medium transition-colors" data-id="element-456">
                Take the Full Store Tour
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-457" />
              </Link>
            </div>
          </div>

          {/* Asymmetric photo collage */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6" data-id="element-458">
            {/* Large featured image */}
            <div className="lg:col-span-8 overflow-hidden rounded-sm shadow-md group" data-id="element-459">
              <img src="https://cdn.magicpatterns.com/uploads/4PEc13YMi3QHpCzosrhrkQ/store-interior-firearms-wide.jpg" alt="Wide view of the firearms floor at Kjergaard Sports — racks of rifles and shotguns" className="w-full h-72 md:h-[28rem] lg:h-[32rem] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-460" />
            </div>

            {/* Right side stack */}
            <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6" data-id="element-461">
              <div className="overflow-hidden rounded-sm shadow-md group" data-id="element-462">
                <img src="https://cdn.magicpatterns.com/uploads/1BGu7nrTdE3WRCeMKF6WrW/store-tour-wide.jpg" alt="Main floor and aisles at Kjergaard Sports" className="w-full h-44 md:h-56 lg:h-[15.5rem] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-463" />
              </div>
              <div className="overflow-hidden rounded-sm shadow-md group" data-id="element-464">
                <img src="https://cdn.magicpatterns.com/uploads/kENmASH4b2qj4AE9LqDfT7/mounted-rural-detail.jpg" alt="Mounted game and rural atmosphere at Kjergaard Sports" className="w-full h-44 md:h-56 lg:h-[15.5rem] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-465" />
              </div>
            </div>
          </div>

          {/* Secondary supporting row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-4 md:mt-6" data-id="element-466">
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-467">
              <img src="https://cdn.magicpatterns.com/uploads/w1nfdCex88kVGKJTVUQepJ/ammunition-wall.jpg" alt="Walls of in-store ammunition at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-468" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-469">
              <img src="https://cdn.magicpatterns.com/uploads/8918DCMwwSgbp8VrUA2ZyG/reloading-supplies.jpg" alt="Reloading components and supplies at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-470" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-471">
              <img src="https://cdn.magicpatterns.com/uploads/esrRsJE1XbS5zcQi7PgR9E/safes-display.jpg" alt="Gun safes and secure storage at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-472" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-473">
              <img src="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="In-store gunsmithing workshop at Kjergaard Sports" className="w-full h-32 md:h-40 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-474" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 bg-brand-cream" data-id="element-475">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-476">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-477">
            <div className="order-2 lg:order-1" data-id="element-478">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-479">
                A Store Built Around Real In-Person Help
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed" data-id="element-480">
                Kjergaard Sports is a practical, knowledgeable, local store
                where customers can ask questions, compare products in person,
                and get help choosing the right equipment. We take pride in
                offering humble, small-town service—not a corporate retail
                experience.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-481">
                {services.map((service, index) => <li key={index} className="flex items-start" data-id="element-482">
                    <CheckCircle2 className="w-6 h-6 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-483" />
                    <span className="text-brand-charcoal font-medium" data-id="element-484">
                      {service}
                    </span>
                  </li>)}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-485">
                <Link to="/contact" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium text-center transition-colors" data-id="element-486">
                  Ask a Question
                </Link>
                <a href={PHONE_TEL} className="border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors" data-id="element-487">
                  Call the Store
                </a>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative" data-id="element-488">
              <div className="absolute inset-0 bg-brand-sage translate-x-4 translate-y-4 rounded-sm" data-id="element-489"></div>
              <img src="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="Gunsmithing workshop and in-store support at Kjergaard Sports" className="relative z-10 w-full h-auto rounded-sm shadow-lg object-cover aspect-[4/3]" data-id="element-490" />
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" className="py-20 bg-brand-hunter text-brand-offwhite relative overflow-hidden" data-id="element-491">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-492">
          <img src="https://cdn.magicpatterns.com/uploads/4W49omroAHfAdaTefJNmcK/targets-shooting-supplies.jpg" alt="Reactive steel targets and shooting supplies at Kjergaard Sports" className="w-full h-full object-cover" data-id="element-493" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-494">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-495">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-496">
              Classes, Events &amp; Store Updates
            </h2>
            <div className="h-1 w-20 bg-brand-leather mx-auto" data-id="element-497"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-498">
            {events.map((event, index) => <div key={index} className="bg-brand-charcoal/40 backdrop-blur-sm border border-brand-sage/30 p-8 rounded-sm" data-id="element-499">
                <Calendar className="w-8 h-8 text-brand-leather mb-4" data-id="element-500" />
                <h3 className="text-xl font-serif mb-3" data-id="element-501">{event.title}</h3>
                <p className="text-brand-offwhite/80 mb-6" data-id="element-502">
                  {event.description}
                </p>
                <div className="flex flex-col space-y-3" data-id="element-503">
                  <a href={PHONE_TEL} className="text-sm font-medium text-brand-leather hover:text-brand-bone transition-colors" data-id="element-504">
                    Call for Details →
                  </a>
                </div>
              </div>)}
          </div>

          <div className="bg-brand-charcoal/60 backdrop-blur-md border border-brand-sage/50 p-6 rounded-sm flex items-start max-w-4xl mx-auto" data-id="element-505">
            <Info className="w-6 h-6 text-brand-leather mr-4 flex-shrink-0 mt-1" data-id="element-506" />
            <p className="text-brand-offwhite/90 text-lg" data-id="element-507">
              Class dates and event details change. Call the store or follow
              Kjergaard Sports on Facebook for current updates.
            </p>
          </div>
        </div>
      </section>

      {/* REVIEWS — larger, more readable quotes */}
      <section className="py-24 bg-brand-cream bg-texture" data-id="element-508">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-509">
          <div className="text-center mb-16" data-id="element-510">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-511">
              Why Customers Say It's Worth the Drive
            </h2>
            <div className="h-1 w-20 bg-brand-leather mx-auto" data-id="element-512"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10" data-id="element-513">
            {reviews.map((review, index) => <div key={index} className="flex flex-col bg-brand-offwhite p-8 md:p-10 rounded-sm border border-brand-bone shadow-sm" data-id="element-514">
                <Quote className="w-10 h-10 text-brand-leather mb-5" data-id="element-515" />
                <blockquote className="flex-grow mb-7" data-id="element-516">
                  <p className="text-xl md:text-2xl font-serif text-brand-charcoal leading-snug" data-id="element-517">
                    &ldquo;{review.text}&rdquo;
                  </p>
                </blockquote>
                <div className="pt-5 border-t border-brand-wood/20" data-id="element-518">
                  <p className="font-semibold text-brand-hunter uppercase tracking-wider text-sm" data-id="element-519">
                    — {review.author}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PLAN YOUR VISIT */}
      <section className="py-20 bg-brand-bone" data-id="element-520">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-521">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-522">
            <div className="grid grid-cols-2 gap-4" data-id="element-523">
              <div className="col-span-2" data-id="element-524">
                <img src="https://cdn.magicpatterns.com/uploads/f5Jp1yjaV9t3nwDxVsC5wU/exterior-front-building.jpg" alt="Kjergaard Sports storefront in Lake Benton, Minnesota" className="w-full h-64 object-cover rounded-sm shadow-sm" data-id="element-525" />
              </div>
              <div data-id="element-526">
                <img src="https://cdn.magicpatterns.com/uploads/r78gFzpmsNjcdavFnWNwrr/property-sign.jpg" alt="Kjergaard Sports property sign" className="w-full h-48 object-cover rounded-sm shadow-sm" data-id="element-527" />
              </div>
              <div data-id="element-528">
                <img src="https://cdn.magicpatterns.com/uploads/pvSYjNceL5zo92xh1cTiRL/road-sign.jpg" alt="Kjergaard Sports road sign" className="w-full h-48 object-cover rounded-sm shadow-sm" data-id="element-529" />
              </div>
            </div>

            <div data-id="element-530">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-531">
                Plan Your Visit to Lake Benton
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed" data-id="element-532">
                We are an out-of-town destination store, and we know our
                customers travel to see us. When you make the trip, you'll find
                a massive selection and staff ready to help.
              </p>

              <div className="space-y-6 mb-10" data-id="element-533">
                <div className="flex items-start" data-id="element-534">
                  <MapPin className="w-6 h-6 text-brand-hunter mr-4 mt-1 flex-shrink-0" data-id="element-535" />
                  <div data-id="element-536">
                    <h3 className="font-medium text-brand-charcoal text-lg mb-1" data-id="element-537">
                      Address
                    </h3>
                    <p className="text-brand-charcoal/80" data-id="element-538">
                      {ADDRESS_LINE_1}
                      <br data-id="element-539" />
                      {ADDRESS_LINE_2}
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-540">
                  <Phone className="w-6 h-6 text-brand-hunter mr-4 mt-1 flex-shrink-0" data-id="element-541" />
                  <div data-id="element-542">
                    <h3 className="font-medium text-brand-charcoal text-lg mb-1" data-id="element-543">
                      Phone
                    </h3>
                    <p className="text-brand-charcoal/80" data-id="element-544">{PHONE}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-545">
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-546">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-547" />
                  Get Directions
                </a>
                <a href={PHONE_TEL} className="border border-brand-charcoal/20 hover:border-brand-leather hover:text-brand-leather text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-548">
                  <Phone className="w-5 h-5 mr-2" data-id="element-549" />
                  Call {PHONE}
                </a>
                <Link to="/contact" className="bg-brand-offwhite hover:bg-brand-cream text-brand-charcoal px-8 py-3 rounded-sm font-medium text-center transition-colors border border-brand-cream" data-id="element-550">
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}